'use client'

import { Text } from '@components/Helpers'
import { Box, styled } from '@mui/material'
import Image from 'next/image'
import { DewiColorsDark4, DewiColorsDark8 } from '@styles/tokens/dist/js/tokens'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import ColorBar, { ColorSection } from '@components/ColorBar'
import { PublicKey } from '@solana/web3.js'
import { useProposal } from '@helium/modular-governance-hooks'
import LoadingSpinner from '@components/LoadingSpinner'
import { useTranslation } from 'react-i18next'
import AvatarList from '../AvatarList'
import FormCard from '../FormCard'
import Editor from '@components/Editor'
import { useVotes } from '@hooks/useVotes'
import ProposalDialog from './ProposalDialog'
import AllVotesDialog from './AllVotesDialog'
import ExplorerButtons from '@components/ExplorerButtons'
import DewiButton from '@components/DewiButton'
import Proposer from '../Proposer'
import { useProposalMetadata } from '@hooks/useProposalMetadata'

type ProposalDetails = {
  markdown: string
}

function parseMarkdown(markdown: string | any | undefined): ProposalDetails[] {
  try {
    const details: ProposalDetails[] = JSON.parse(
      (markdown as string) || '[]',
    ).map((detail: { markdown?: string }) => detail)
    return details
  } catch (e) {
    return markdown.map((detail: { markdown?: string }) => detail)
  }
}

const BoldText = {
  fontWeight: 'bold',
}

const ProposalImage = styled(Image)({
  width: '100%',
  height: '100%',
  borderRadius: '1rem',
})

// Keeps the header block intact for proposals whose artwork is gone, instead
// of a broken image.
const ProposalImagePlaceholder = styled(Box)({
  width: '100%',
  height: 320,
  borderRadius: '1rem',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
})

const MetadataLostText = {
  fontSize: '0.875rem',
  marginBottom: '1rem',
  // Color missing from design system
  color: '#FFFFFF80',
}

const ProposalVotesGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-evenly',
  marginBottom: '1rem',
  gap: '1rem',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}))

const ProposalTitleGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const ImageFormCard = { marginBottom: '1rem', borderRadius: '1rem' }

const ColorBarGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '0.5rem',
})

const AvatarListGroup = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
})

const VoteFormCard = (choice: number) => ({
  padding: '1rem',
  backgroundColor: DewiColorsDark8,
  border: `1px solid ${DewiColorsDark4}`,
  width: '-webkit-fill-available',
})

const VoteColorBar = {
  height: '0.7rem',
}

const VoteCountText = {
  minWidth: 'fit-content',
  marginLeft: '0.5rem',
  fontSize: '0.7rem',
  // Color missing from design system
  color: '#FFFFFF80',
}

const ProposalTitleText = {
  fontWeight: 'bold',
  fontSize: '2rem',
}

const LoaderLayout = styled(Box)(() => ({
  width: '100dvw',
  height: '100dvh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}))

const Container = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('xl')]: {
    width: '90%',
  },
  [theme.breakpoints.up('xl')]: {
    width: '65%',
  },
}))

type ProposalDetailsProps = {
  proposalKey: string
}

function ProposalDetails({ proposalKey }: ProposalDetailsProps) {
  const [details, setDetails] = useState<ProposalDetails[]>([])
  const [showAllVotes, setShowAllVotes] = useState(false)
  const { markers, calculateTotalVotes, calculateVotesForChoice } = useVotes(
    new PublicKey(proposalKey),
  )

  const proposalPublicKey = useMemo(
    () => (proposalKey ? new PublicKey(proposalKey) : undefined),
    [proposalKey],
  )

  const proposal = useProposal(proposalPublicKey)
  const { t } = useTranslation()

  // Shadow Drive took most proposal uris with it; the recovery map resolves the
  // self-hosted copy, or tells us the metadata is gone, before we fetch.
  const {
    metadata: proposalMetadata,
    imageUri,
    isUnavailable,
    isLoading: metadataLoading,
  } = useProposalMetadata(proposalKey, proposal?.info?.uri)

  const onShowAllVotes = useCallback(() => {
    setShowAllVotes(true)
    // Find document hmtl and disable scrolling
    const documentElement = document.documentElement
    documentElement.style.overflow = 'hidden'
  }, [setShowAllVotes])

  const handleCloseAllVotes = useCallback(() => {
    setShowAllVotes(false)
    // Find document hmtl and enable scrolling
    const documentElement = document.documentElement
    documentElement.style.overflow = ''
  }, [setShowAllVotes])

  useEffect(() => {
    setDetails(parseMarkdown(proposalMetadata?.metadata))
  }, [proposalMetadata])

  const colorBarSections = useCallback(
    (colorBarIndex: number): ColorSection[] => {
      const total = calculateTotalVotes(markers)

      let sections = proposal?.info?.choices.map((choice, index) => {
        const totalVotesForChoice = calculateVotesForChoice(markers, index)
        const percent =
          totalVotesForChoice > 0 ? (totalVotesForChoice / total) * 100 : 0
        let colorType = 'green'

        if (proposal?.info?.choices.length === 3) {
          if (index === 0) {
            colorType = 'green'
          } else if (index === 1) {
            colorType = 'red'
          } else {
            colorType = 'gray'
          }
        } else {
          if (index === 0) {
            colorType = 'cyan'
          } else if (index === 1) {
            colorType = 'orange'
          } else if (index === 2) {
            colorType = 'violet'
          } else {
            colorType = 'pink'
          }
        }

        return {
          colorType,
          percent,
          isActive: colorBarIndex === index,
        }
      }) as ColorSection[]

      if (sections?.reduce((acc, section) => acc + section.percent, 0) === 0) {
        sections = sections.map((section) => ({
          ...section,
          percent: 100,
        }))
      }

      return sections
    },
    [calculateTotalVotes, calculateVotesForChoice, markers, proposal],
  )

  if (!proposalKey || !proposal?.info || metadataLoading) {
    return (
      <LoaderLayout>
        <LoadingSpinner size={40} />
      </LoaderLayout>
    )
  }

  return (
    <Container>
      <ProposalTitleGroup>
        <Text sx={ProposalTitleText}>{proposal?.info?.name}</Text>
        <ExplorerButtons signature={proposalKey} />
      </ProposalTitleGroup>
      <FormCard sx={ImageFormCard}>
        {imageUri || isUnavailable ? (
          <ProposalImage
            alt={proposal?.info?.name}
            src={imageUri || '/proposals/lost.png'}
            width={2000}
            height={2000}
          />
        ) : (
          <ProposalImagePlaceholder />
        )}
      </FormCard>
      {isUnavailable && (
        <div className="flex flex-col items-start gap-3">
          <Text sx={MetadataLostText}>{t('dao.metadataLost')}</Text>
          <a
            href="https://discord.gg/ZFZ2Xa3Q8A"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-gray-0 px-5 py-2 font-sans text-sm font-bold text-black"
          >
            {t('dao.discordCta')}
          </a>
        </div>
      )}
      {proposalMetadata?.proposer && (
        <Proposer proposerKey={proposalMetadata?.proposer} />
      )}
      <ProposalVotesGroup>
        {proposal?.info?.choices.map((choice, index) => (
          <FormCard key={choice.name} sx={VoteFormCard(index)}>
            <Text sx={BoldText}>{choice.name}</Text>
            <ColorBarGroup>
              <ColorBar sections={colorBarSections(index)} sx={VoteColorBar} />
              <Text sx={VoteCountText}>
                {calculateVotesForChoice(markers, index)}
              </Text>
            </ColorBarGroup>
            <AvatarListGroup>
              <AvatarList
                images={markers
                  ?.filter((marker: any) => marker.choices.includes(index))
                  .map((marker: any) => marker.image)}
                total={calculateVotesForChoice(markers, index)}
                max={5}
              />
            </AvatarListGroup>
          </FormCard>
        ))}
      </ProposalVotesGroup>
      <Box flex={1} alignItems={'center'} display={'flex'}>
        <DewiButton
          variant="text"
          onClick={onShowAllVotes}
          sx={{ flex: 1, marginBottom: 'var(--dewi_spacing_4)' }}
        >
          View all votes
        </DewiButton>
      </Box>
      <Box
        display={'flex'}
        flexDirection={'column'}
        sx={{
          gap: 'var(--dewi_spacing_4)',
          marginBottom: 'var(--dewi_spacing_12)',
        }}
      >
        {details?.map((detail, index) => {
          return (
            <Editor
              disabled
              defaultEditorContent={JSON.parse(detail.markdown || '[]')}
              key={`${index} 1`}
              onContentSet={() => {}}
            />
          )
        })}
      </Box>
      <ProposalDialog proposalKey={proposalKey} />
      <AllVotesDialog
        proposalKey={proposalKey}
        open={showAllVotes}
        onClose={handleCloseAllVotes}
      />
    </Container>
  )
}

export default memo(ProposalDetails)
